import { Translations } from './types';

export const en: Translations = {
  // Common
  save: 'Save',
  saving: 'Saving…',
  cancel: 'Cancel',
  close: 'Close',
  delete: 'Delete',
  create: 'Create',
  add: 'Add',
  apply: 'Apply',
  back: 'Back',
  next: 'Next',
  done: 'Done ✓',
  loading: 'Loading…',
  copy: 'Copy',
  copied: 'Copied!',
  restore: 'Restore',
  send: 'Send',
  invite: 'Invite',
  untitled: 'Untitled',
  you: '(you)',
  unknown: 'Unknown',
  saveFailed: 'Failed to save. Please try again.',

  // Topbar (flowchart)
  appSubtitleFlow: 'FlowPlanner-mode',
  projectPlaceholder: 'Project...',
  new: 'New',
  open: 'Open',
  history: 'History',
  share: 'Share',
  controls: 'Controls',
  logOut: 'Log out',
  simple: 'Simple',
  advanced: 'Advanced',
  hours: 'Hours',
  days: 'Days',
  weeks: 'Weeks',
  showStartEnd: 'Show Start&End:',
  startEndModeDate: 'Date',
  startEndModeOffset: 'Offset',
  endDisplayDay: 'Day',
  endDisplayDate: 'Date',
  endDisplayYear: 'Year',
  endDisplayTime: 'Time',
  showOptionsBtn: 'View',
  showComments: 'Comments',
  showSubitemBadgesLabel: 'Subitems',
  quickConnectOnHint: 'Quick Connect ON — click boxes to connect (ESC or Ctrl+Q to disable)',
  quickConnectOffHint: 'Quick Connect OFF — enable to connect boxes with 2 clicks (Ctrl+Q)',
  gantt: 'Gantt',
  project: 'Project',
  projectGanttTitle: 'Project Gantt: {{name}}',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  historyDisabledHint: 'Save the flowchart first to view history',
  historyHint: 'Version history',
  shareDisabledHint: 'Save the flowchart first to share it',
  shareHint: 'Share this flowchart',
  usersOnline: '{{count}} user(s) online',

  // Sidebar
  addLabel: 'ADD',
  editLabel: 'EDIT',
  box: 'Box',
  note: 'Comment',
  start: 'Start',
  category: 'Category',
  resources: 'Resources',
  area: 'Top-level',
  viewAllFields: 'View all fields',
  viewAllFieldsHint: 'Always show and allow editing duration/assignee fields on every box, instead of only on the one you have selected',
  undo: 'Undo',
  redo: 'Redo',
  critical: 'Critical',
  export: 'Export',
  exportXlsxHint: 'Task list with IDs and predecessors — for import into MS Project or similar tools',
  home: 'Home',
  homeActive: 'Home ✓',
  addBox: 'Add Box',
  addNote: 'Add Note',
  addStartDay: 'Add Start Day',
  toggleCategory: 'Toggle Categories',
  toggleResources: 'Toggle Resources',
  addResource: '+ Add Resource',
  newResourceName: 'New name',
  deleteResource: 'Delete resource',
  noResourcesYet: 'No resources yet — add team member names to assign them to boxes.',
  duplicateResourceName: 'Another resource already has this exact name — box assignment, Gantt filtering, and resource hours all identify resources by name, so these will be treated as the same resource. Rename one to keep them separate.',
  duplicateResourceNameBlocked: 'A resource with this exact name already exists. Every resource needs a unique name — please choose a different one.',
  duplicateResourceNameWarning: 'Two or more resources have the exact same name. Since assignment and filtering match by name, they\'ll be treated as one resource. Give each a unique name to keep them separate.',
  assignResources: 'Assign resources',
  team: 'Team',
  linkCategory: 'Link to category (sets the resource\'s color)',
  noCategory: 'No category',
  noTeam: 'No team',
  dragToReorderResource: 'Drag to reorder within team',
  areaModeActiveHint: 'Top-level draw mode active — drag on canvas to create a top-level (click again to stop)',
  areaModeHint: 'Add top-level — drag a rectangle on the canvas',
  undoHint: 'Undo (Ctrl+Z)',
  redoHint: 'Redo (Ctrl+Y)',
  showCriticalPathHint: 'Show critical path on canvas',
  homeSaveFirstHint: 'Save first to set as default',
  homeRemoveHint: 'Remove as default',
  homeSetHint: 'Set as default (opens automatically)',
  flowchartModeActive: 'Flowchart mode (active)',
  switchToMindmap: 'Switch to Mindmap mode',
  flowMode: 'Flow',
  mindMode: 'Mind',

  // Modals / panels
  categories: 'Categories',
  deleteDepartment: 'Delete department',
  addCategory: '+ Add Category',
  newCategory: 'New Category',
  changeLog: 'Change log',
  noChangesLogged: 'No changes logged yet.',
  changesTrackedAfterSave: 'Changes are tracked after each save.',
  controlsReminder: 'Controls reminder',
  restartTour: 'Restart tour →',
  link: 'Link',
  openLink: 'Open link',
  deleteLink: 'Delete link',
  modifyLink: 'Modify link',
  setAutomaticDuration: 'Set automatic duration',
  setAutomaticDurationTitle: 'Set end date/time for automatic duration calculation.',
  setAutomaticDurationIntro: 'Select the desired end moment for {{box}}. The duration will be calculated automatically based on the start time, considering weekends, holidays, and the task\'s execution mode.',
  addLink: 'Add link',
  addHyperlink: 'Add hyperlink',
  setDate: 'Set date',
  earliestStart: 'Earliest start',
  execution247: '24/7 execution',
  execution245: '24/5 execution',
  makeMilestone: 'Make milestone',
  editSubitems: 'Edit subitems',
  addUid: 'Add UID',
  editUid: 'Edit UID',
  deleteUid: 'Delete UID',
  uidLabel: 'UID',
  copyAction: 'Copy',
  duplicate: 'Duplicate',
  resizeNote: 'Resize comment',
  makePrivateComment: 'Make private comment',
  removePrivateComment: 'Make public again',
  privateCommentBadgeHint: 'Private comment — only visible to you',
  paste: 'Paste',
  selectAll: 'Select all',
  setStartDate: 'Set start date',
  setStartDateTitle: 'Set start date',
  startConstraintTypeLabel: 'Type',
  startConstraintTypeFixed: 'Fixed start date',
  startConstraintTypeEarliest: 'Do not start before',
  earliestStartIntro: '{{box}} may not start before:',
  thisBox: 'This box',
  weekNumber: 'Week number',
  weekPlaceholder: 'e.g. 14',
  notBeforeWeek: 'Not before week {{week}}',
  date: 'Date',
  time: 'Time',
  optional: '(optional)',
  removeConstraint: 'Remove constraint',
  setDateIntro: '{{box}} will be pinned to start on this date, ignoring the dependency chain.',
  thisTask: 'This task',
  deleteConnection: 'Delete connection',
  resizeNoteTitle: 'Resize note',
  widthPx: 'Width (px)',
  heightPx: 'Height (px)',
  unsavedWorkFound: 'Unsaved work found from {{date}}.',
  discard: 'Discard',
  updatedBy: 'Updated by',

  // Controls reminder rows
  ctrlScrollWheel: 'Scroll wheel',
  ctrlZoom: 'Zoom in / out',
  ctrlRightDrag: 'Right-click + drag',
  ctrlPan: 'Pan the canvas',
  ctrlLeftDrag: 'Left-click + drag',
  ctrlSelectMultiple: 'Select multiple objects',
  ctrlDragCanvas: 'Drag on canvas',
  ctrlDrawBox: 'Draw a new box',
  ctrlCopyPaste: 'Copy / paste boxes',
  ctrlUndoRedo: 'Undo / redo',
  ctrlQuickConnect: 'Toggle Quick Connect',
  ctrlDelete: 'Delete',
  ctrlDeleteSelected: 'Delete selected',
  ctrlSave: 'Save',

  // Alerts / confirms (flowchart)
  needEditorAccessBoxes: 'You need editor access to add boxes',
  needEditorAccessNotes: 'You need editor access to add comments',
  commentHiddenInViewWarning: 'This comment will be placed, but comments are currently hidden (View settings) — turn on "Comments" to see it.',
  needEditorAccessStartDays: 'You need editor access to add start days',
  needEditorAccessDeleteBoxes: 'You need editor access to delete boxes',
  needEditorAccessConnections: 'You need editor access to create connections',
  needEditorAccessDeleteConnections: 'You need editor access to delete connections',
  circularReference: 'This connection is not allowed: it would create a circular reference.',
  confirmNewFlowchart: 'Create new flowchart? Unsaved changes will be lost.',
  confirmTrashFlowchart: 'Move this flowchart to trash?',
  confirmTrashMindmap: 'Move this mindmap to trash?',
  deleteFailed: 'Failed to delete. Please try again.',
  saveHomeFailed: 'Failed to save home setting. Please try again.',
  couldNotLoadShared: 'Could not load the shared flowchart. Please try again.',
  flowchartNotFound: 'Flowchart not found.',
  invalidShareLink: 'This share link is invalid or has expired.',
  couldNotAcceptInvitation: 'Could not accept invitation: {{msg}}',
  projectAccessGranted: 'You now have {{role}} access to this project. Open it from your project list.',
  accessGrantedOpenManually: 'Access granted! The flowchart could not be loaded automatically — please open it from your list.',
  somethingWentWrongInvitation: 'Something went wrong accepting the invitation. Please try again.',

  // Project list
  projectsAndFlowcharts: 'Projects & Flowcharts',
  mindmaps: 'Mindmaps',
  projectsCount: '{{count}} project',
  projectsCountPlural: '{{count}} projects',
  flowchartsCount: '{{count}} flowchart',
  flowchartsCountPlural: '{{count}} flowcharts',
  mindmapsCount: '{{count}} mindmap',
  mindmapsCountPlural: '{{count}} mindmaps',
  newProject: 'New Project',
  newProjectMaxReached: 'New Project (Max 10 reached)',
  projectNamePlaceholder: 'Project name...',
  chooseColor: 'Choose Color',
  createProject: 'Create Project',
  loadingProjects: 'Loading projects...',
  newFlowchartInProject: 'New flowchart in project',
  newMindmapInProject: 'New mindmap in project',
  shareProject: 'Share project',
  changeColor: 'Change color',
  renameProject: 'Rename project',
  deleteProject: 'Delete project',
  leaveProject: 'Leave project',
  noFlowchartsInProject: 'No flowcharts in this project yet',
  noMindmapsInProject: 'No mindmaps in this project yet',
  clickPlusFlowchart: 'Click + to create a flowchart',
  clickPlusMindmap: 'Click + to create a mindmap',
  moveTo: 'Move to...',
  ungrouped: 'Ungrouped',
  versionHistory: 'Version history',
  flowchartTitlePlaceholder: 'Flowchart title...',
  mindmapTitlePlaceholder: 'Mindmap title...',
  trash: 'Trash',
  deletedAgo: 'Deleted {{date}}',
  daysLeft: '{{days}}d left',
  deletingSoon: 'deleting soon',
  deletePermanently: 'Delete permanently',
  autoDeleteInfo: 'Items are automatically deleted after 30 days',
  noProjectsYet: 'No projects yet',
  createFirstProjectDesc: 'Create your first project to organize your flowcharts',
  createFirstProject: 'Create First Project',
  noMindmapsYet: 'No mindmaps yet',
  createFirstMindmapDesc: 'Create your first mindmap via the New button in the toolbar',
  noSavedVersions: 'No saved versions found.',
  latest: '← Latest',
  enterProjectName: 'Please enter a project name',
  maxProjects: 'Maximum 10 projects allowed',
  createProjectFailed: 'Failed to create project. Please try again.',
  updateProjectFailed: 'Failed to update project. Please try again.',
  updateProjectColorFailed: 'Failed to update project color. Please try again.',
  deleteProjectFailed: 'Failed to delete project. Please try again.',
  leaveProjectFailed: 'Failed to leave project. Please try again.',
  leaveFlowchart: 'Leave',
  leaveFlowchartFailed: 'Failed to leave. Please try again.',
  confirmLeaveFlowchart: 'You will lose access to this item. It will remain for its owner and other collaborators.',
  moveFlowchartFailed: 'Failed to move flowchart. Please try again.',
  loadProjectsFailed: 'Failed to load projects. Please refresh the page.',
  duplicateFailed: 'Duplicate failed',
  restoreFlowchartFailed: 'Failed to restore flowchart. Please try again.',
  createFlowchartFailed: 'Failed to create flowchart. Please try again.',
  confirmRestoreVersion: 'Restore this version? Unsaved changes will be overwritten.',
  couldNotLoadVersion: 'Could not load this version. Please try again.',
  confirmDeleteProject: 'Are you sure you want to delete this project? This action cannot be undone.',
  confirmDeleteProjectWithFlowcharts: 'This project contains flowcharts. They will be moved to Ungrouped. This action cannot be undone.',
  confirmLeaveProject: 'You will lose access to this project and its flowcharts. The project will remain for other members.',
  confirmPermanentDelete: 'This will permanently delete the flowchart and all its version history. This cannot be undone.',
  deleteForever: 'Delete Forever',
  leave: 'Leave',
  confirm: 'Confirm',

  // Share dialog (flowchart)
  shareLink: 'Share link',
  enableLinkSharingHint: 'Enable link sharing to create a shareable link. Anyone with the link can view this flowchart.',
  inviteByEmail: 'Invite by email',
  emailPlaceholder: 'email@example.com',
  editor: 'Editor',
  viewer: 'Viewer',
  owner: 'Owner',
  addMessageOptional: 'Add a message (optional)',
  peopleWithAccess: 'People with access',
  viaProjectSharing: 'Via project sharing',
  addedOn: 'Added {{date}}',
  projectBadge: 'Project',
  makeOwner: 'Make owner',
  changeToEditor: 'Change to Editor',
  changeToViewer: 'Change to Viewer',
  removeAccess: 'Remove access',
  pendingInvitations: 'Pending invitations',
  invitedOn: 'Invited {{date}}',
  youAreRole: 'You are {{article}} {{role}}',
  youHaveNoAccess: 'You have no access',
  invitationSent: 'Invitation sent to {{email}}',
  invitationFailed: 'Failed to send invitation: {{error}}',
  failedUpdateRole: 'Failed to update role',
  failedRemoveUser: 'Failed to remove user',
  failedCancelInvitation: 'Failed to cancel invitation',
  confirmRemoveUserAccess: "Remove this user's access?",
  failedCopyLink: 'Failed to copy link to clipboard',
  failedUpdateLinkSharing: 'Failed to update link sharing settings',

  // Share project dialog
  shareProjectTitle: 'Share Project',
  anyoneWithLinkProject: 'Anyone with this link can access this project and all its flowcharts',
  enableLinkSharingProjectHint: 'Enable link sharing to allow anyone with the link to access this project',
  linkAccess: 'Link access:',
  canView: 'Can view',
  canEdit: 'Can edit',
  colleaguePlaceholder: 'colleague@example.com',
  noUsersYet: 'No users yet',
  invitationPending: 'Invitation pending',
  cancelInvitation: 'Cancel invitation',
  removeUser: 'Remove user',
  unknownUser: 'Unknown User',
  sharingProjectIncludes: 'Sharing a project includes:',
  includesAllFlowcharts: 'All flowcharts in this project',
  includesFutureFlowcharts: 'Future flowcharts added to this project',
  includesCreatePermission: 'Permission to create new flowcharts in this project (for editors)',
  enterEmailAddress: 'Please enter an email address',
  enterValidEmail: 'Please enter a valid email address',
  failedSendInvitation: 'Failed to send invitation',
  failedRemoveUserProject: 'Failed to remove user',
  failedUpdateUserRole: 'Failed to update user role',
  failedUpdateAccessRole: 'Failed to update access role',
  confirmRemoveUserProject: 'Remove this user from the project?',

  // Error boundary
  somethingWentWrong: 'Something went wrong',
  somethingWentWrongIn: 'Something went wrong in {{label}}',
  unexpectedError: 'An unexpected error occurred. Your work is auto-saved — reload to continue.',
  reloadPage: 'Reload page',

  // Auth (App)
  signInToContinue: 'Sign in to continue',
  createYourAccount: 'Create your account',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  minChars: 'Minimum 6 characters',
  signIn: 'Sign In',
  signUp: 'Sign Up',
  noAccountSignUp: "Don't have an account? Sign up",
  haveAccountSignIn: 'Already have an account? Sign in',
  forgotPassword: 'Forgot password?',
  passwordResetSent: 'Password reset email sent. Check your inbox.',
  passwordResetFailed: 'Could not send the password reset email. Please try again later.',
  checkEmailConfirm: 'Check your email to confirm your account!',
  authFailed: 'Authentication failed',
  sessionExpiredElsewhere: 'You were signed out — your session ended, possibly because you signed in elsewhere. Please log in again.',
  enterEmailFirst: 'Enter your email address first.',
  awaitingApproval: 'Awaiting approval',
  awaitingApprovalDesc: 'Your account ({{email}}) has been created and is waiting for an administrator to approve access.',
  awaitingApprovalNote: "You'll be able to sign in once your account is approved.",
  signOut: 'Sign out',
  newVersionAvailable: 'A new version of MindYourPlan is available. Please reload to avoid compatibility issues.',
  reloadNow: 'Reload now',

  // Tour
  tourStepOf: 'STEP {{current}} OF {{total}}',
  tourTitleName: 'Flowchart name',
  tourDescName: 'Give your flowchart a clear name here. It appears in your project list and in the Gantt chart header.',
  tourTitleNew: 'New flowchart',
  tourDescNew: 'Start with a blank canvas. Opens in Simple mode so you can focus on the structure first.',
  tourTitleOpen: 'Open',
  tourDescOpen: 'Browse all your saved flowcharts and projects. You can organise flowcharts into project folders here.',
  tourTitleSave: 'Save',
  tourDescSave: 'Saves your flowchart to the database. With Auto-save enabled, changes sync automatically as you work.',
  tourTitleShare: 'Share',
  tourDescShare: 'Invite teammates by email or generate a shareable link. Assign them as Viewer or Editor. Available once the flowchart is saved.',
  tourTitleAutosave: 'Auto-save',
  tourDescAutosave: 'When on, every change is saved automatically and synced in real-time with collaborators viewing the same flowchart.',
  tourTitleMode: 'View options',
  tourDescMode: 'Select a box to edit its duration, assignee and completion fields. Turn on "View all fields" here to always show and edit them on every box instead — ideal for Gantt planning.',
  tourTitleConnect: 'Quick Connect',
  tourDescConnect: 'Enable this, then click two boxes to draw an arrow between them — no dragging needed. Press Ctrl+Q or Esc to toggle.',
  tourTitleGantt: 'Gantt chart',
  tourDescGantt: 'Generates a Gantt timeline from your flowchart. Fill in durations and assignees on boxes in Advanced mode for the best result.',
  tourTitleZoom: 'Zoom',
  tourDescZoom: 'Zoom in/out with these buttons or scroll the mouse wheel over the canvas.',
  tourTitleAddBox: 'Add box',
  tourDescAddBox: 'Adds a task box to the canvas. Tip: you can also drag directly on the canvas to draw a box at any size and position.',
  tourTitleAddNote: 'Add note',
  tourDescAddNote: "Adds a callout bubble — useful for annotations, decisions, or context that doesn't belong in a task box.",
  tourTitleStartDay: 'Start day',
  tourDescStartDay: 'Pins a timeline anchor to the canvas. Boxes connected after a Start Day inherit its offset — great for parallel tracks in your Gantt chart.',
  tourTitleCategory: 'Categories',
  tourDescCategory: 'Define teams or departments and assign a colour to each. Boxes pick up their category colour automatically.',
  tourTitleResources: 'Resources',
  tourDescResources: 'Add the names of your team members here, then assign one or more of them to a box from its Name field.',
  tourTitleCanvas: 'Canvas controls',

  // Gantt panel
  ganttChartTitle: 'Gantt Chart: {{title}}',
  show: 'Show:',
  quarters: 'Quarters',
  months: 'Months',
  weeksLevel: 'Weeks',
  daysLevel: 'Days',
  hoursLevel: 'Hours',
  workDaysOnly: 'Work days only',
  workdayMode: 'Workday',
  workdayHours: 'Workday hours',
  timelineBtn: 'Timeline',
  ganttDurationLabel: 'Duration',
  hideNonWorkingDay: 'Hide non-working days',
  hideNonWorkingHours: 'Hide non-working hours',
  showSubitemsInGantt: 'Show subitems',
  showAreaGroupsInGantt: 'Show area toplevel',
  showRegularBoxesInGantt: 'Show regular boxes',
  showStartEndColumnInGantt: 'Show start/end column',
  end: 'End',
  savedTimelines: 'Saved timelines',
  saveAsPreset: 'Save as preset',
  presetNamePlaceholder: 'Name',
  applyPreset: 'Apply',
  useThisSavedPreset: 'Use this saved preset',
  showInFlowplan: 'Show in Flowplan',
  selectSpecificResourceTimes: 'Select specific resource times',
  cell: 'cell',
  setTimelineDates: 'Set timeline dates',
  timelineFrom: 'From',
  timelineTo: 'To',
  autoFit: 'Auto fit',
  filterByResource: 'Filter by resource',
  filterByCategory: 'Filter by category',
  noMatchingTasks: 'No tasks for this resource',
  clearFilter: 'Clear filter',
  selectAllResources: 'Select all',
  resourceSummaryMode: 'One summary row per resource',
  planSettings: 'Plan Settings',
  planDefaultMode: 'Default mode',
  planStartDate: 'Project start date',
  planHolidays: 'Holidays / non-working days',
  planWorkDays: 'Working days',
  noHolidays: 'No holidays added yet',
  hrsPerDay: '24 hrs/day',
  holidays: 'Holidays',
  showAllDependencies: 'Show all dependencies',
  criticalPath: 'Critical path',
  autoSort: 'Auto sort',
  autoSortHint: 'Reorder rows to minimize total dependency arrow length',
  backToFlowPlanner: 'Back to FlowPlanner',
  exportGantt: 'Export Gantt',
  hideEmptyGanttRows: 'Hide empty rows',
  hideEmptyGanttRowsHint: 'Rows with no bar in the current timeline view are hidden, both on screen and in the export.',
  filteredExport: 'Filtered Export',
  onlyExportSelectedRows: 'Only export selected rows',
  switchViewToSelectRows: 'Switch view to select rows for export',
  onlyExportSelectedRowsHint: 'Check the rows to include using the checkbox next to each one — while this is on, the Gantt view shows them in the order they’ll be exported in. Turn it off to see the normal order and export everything as usual.',
  switchViewToSelectRowsHideEmptyNote: 'Note: if enabled, the "Hide empty rows" option will be automatically disabled to allow dragging/rearranging rows.',
  selectAllRows: 'Select all',
  selectNoRows: 'Select none',
  rowsSelectedCount: '{{count}} of {{total}} rows selected',
  savedExportSelections: 'Saved selections',
  dragToReorder: 'Drag to reorder',
  setDateMarker: 'Set date: {{date}}',
  earliestStartMarker: 'Earliest start: {{value}}',
  nonWorkingDays: 'Non-working days',
  nonWorkingDaysDesc: 'These days are skipped in the Gantt timeline',
  noHolidaysYet: 'No holidays added yet',
  weekAbbr: 'Wk',
  clickToRemove: 'Click to remove',
  clickToAddNonWorking: 'Click to add as non-working day',
  task: 'Task',
  reorderArrowHint: 'Reorder rows to minimize total dependency arrow length',

  // Mindmap
  appSubtitleMind: 'Mindmap-mode',
  mindmapPlaceholder: 'Mindmap...',
  shareMindmap: 'Share this mindmap',
  saveFirstHistory: 'Save first to view history',
  saveFirstShare: 'Save first to share',
  collapseToLevel: 'Collapse to level {{level}}',
  expandAll: 'Expand all',
  priorityMatrix: 'Priority Matrix',
  searchMindmapPlaceholder: 'Search text…',
  searchMindmap: 'Search and highlight matching nodes',
  clearSearch: 'Clear search',
  searchMatchCount: '{{count}} match(es) found',
  switchToMono: 'Switch to mono color',
  switchToMulti: 'Switch to multi color',
  addChildNode: 'Add child node (Tab)',
  deleteSelected: 'Delete selected (Delete)',
  hidePriorities: 'Hide priorities',
  showPriorities: 'Show priorities',
  setPriority: 'Set priority',
  priority: 'Priority {{p}}',
  removePriority: 'Remove priority',
  noPriority: 'No priority',
  highlightNode: 'Highlight',
  removeHighlight: 'Remove highlight',
  markCompleted: 'Mark as completed',
  removeCompleted: 'Mark as not completed',
  completedRemovePrio: 'Remove all completed',
  completedRemovePrioTitle: 'Unassign all completed items from the matrix — the mindmap nodes themselves are kept',
  completedDeleteAll: 'Remove all completed, also from mindmap',
  completedDeleteAllTitle: 'Permanently delete all completed items (and their sub-items) from the mindmap — cannot be undone',
  confirmDeleteCompletedNodes: 'Permanently delete {{count}} completed item(s) (and any sub-items) from the mindmap? This cannot be undone.',
  scheduleDaysOption: '{{n}} days',
  scheduleDaysTitle: 'Number of day-columns shown in the Schedule view',
  addPriorityNode: 'Add priority',
  addPriorityNodeTitle: 'Add a new mindmap item directly into this quadrant/day',
  addPriorityBranchLabel: 'Branch',
  addPriorityQuadrantLabel: 'Quadrant',
  addPriorityValuePlaceholder: 'Enter text...',
  editPriorityTitle: 'Edit title',
  backToMindmap: 'Back to mindmap',
  switchToFlowchart: 'Switch to Flowchart mode',
  mindmapModeActive: 'Mindmap mode (active)',
  setAsDefaultMindmap: 'Set as default mindmap (opens automatically)',
  removeAsDefault: 'Click to remove as default',
  saveFirstDefault: 'Save first to set as default',
  notePlaceholder: 'Type your note here...',
  noteTitle: 'Note',
  bold: 'Bold',
  italic: 'Italic',
  underline: 'Underline',
  bulletList: 'Bullet list',
  numberedList: 'Numbered list',
  textLarger: 'Larger text',
  textSmaller: 'Smaller text',
  increaseIndent: 'Increase indent',
  decreaseIndent: 'Decrease indent',
  cannotLinkCircular: 'Cannot link: circular reference detected.',
  centralTopic: 'Central Topic',
  priorities: 'Priorities',
  multiColor: 'Multi-color',
  monoColor: 'Mono-color',
  childNode: 'Child',
  priorityLabel: 'Priority',
  deleteNote: 'Delete note',
  addChild: 'Add child',
  modifyNote: 'Modify note',
  addNoteAction: 'Add note',
  rename: 'Rename',
  moveToNode: 'Move to…',
  unlinkMindmap: 'Unlink mindmap',
  linkMindmap: 'Link mindmap…',
  linkMindmapTitle: 'Link mindmap',
  linkMindmapDesc: 'The selected mindmap will be attached as a subtree to the selected node.',
  noOtherMindmaps: 'No other mindmaps available.',
  emptyNode: '(empty)',
  mmCtrlDblClick: 'Double-click node',
  mmEditText: 'Edit text',
  mmCtrlDrag: 'Click + drag',
  mmMoveNode: 'Move node',
  mmShiftDrag: 'Shift + drag',
  mmMoveParent: 'Move to different parent',
  mmSelectMultiple: 'Select multiple nodes',
  mmScrollDrag: 'Scroll / right drag',
  mmZoomPan: 'Zoom / pan',
  mmCreateChild: 'Create child node',
  mmCreateSibling: 'Create sibling node',
  mmNavigate: 'Navigate between nodes',
  mmDeleteNode: 'Delete node',

  // Subitems panel
  subitemsPanelTitle: 'Subitems',
  subitemAdd: '+ Add subitem',
  subitemDuration: 'Duration',
  subitemAddDependency: '+ Add dependency',
  subitemTotalDuration: 'Total duration',
  subitemUntitled: 'Untitled subitem',
  subitemEmptyState: 'No subitems yet — add one to start breaking this box down into smaller scheduled steps.',
  durationDeterminedBySubitems: 'Determined by subitems',
  durationNowFromSubitems: 'Duration is now determined by subitems',
  durationNowManual: 'Duration is now set at box level again',
};
